import { 
  CHART_COLORS, 
  VEHICLE_COLORS, 
  USER_CLASS_COLORS, 
  EXCLUDED_KEYS,
  HEATMAP_COLORS 
} from '../constants/chartConstants'

export const detectDataFormat = (data) => {
  if (!data || !data.length) return 'unknown';
  
  const firstItem = data[0];
  const keys = Object.keys(firstItem).join(',');
  
  if ('entry_street' in firstItem && 'exit_street' in firstItem && 'value' in firstItem) {
    return 'sankey';
  }

  if (keys.includes('p85') || keys.includes('p50') || keys.includes('p95')) {
    return 'velocity-percentile';
  }
  
  if ('nodes' in firstItem && 'links' in firstItem) {
    return 'sankey';
  }
  
  if ('VelBucket' in firstItem && 'event_time' in firstItem) {
    return 'velocity-pivot';
  }
  
  if ('event_time' in firstItem && !('VelBucket' in firstItem)) {
    const hasNumericKeys = Object.keys(firstItem).some(key => 
      !EXCLUDED_KEYS.has(key) && typeof firstItem[key] === 'number'
    );
    if (hasNumericKeys) return 'time-series';
  }
  
  if ('VelBucket' in firstItem && !('event_time' in firstItem)) {
    return 'velocity-bucket';
  }
  
  if ('speedMPHBucket' in firstItem && 'veh_type' in firstItem) {
    return 'kpi-velocity';
  }
  
  return 'unknown';
};

export const getNumericKeys = (data) => {
  if (!data || !data.length) return [];
  
  return Object.keys(data[0]).filter(key =>
    !EXCLUDED_KEYS.has(key) && typeof data[0][key] === 'number'
  );
};

export const getColorForKey = (key, index) => {
  const baseKey = key.split('_')[0];

  if (VEHICLE_COLORS[baseKey]) {
    return VEHICLE_COLORS[baseKey];
  }
  if (VEHICLE_COLORS[key]) {
    return VEHICLE_COLORS[key];
  }
  return CHART_COLORS[index % CHART_COLORS.length];
};

export const getUserClassColor = (userClass, type = 'node') => {
  if (!userClass) {
    return type === 'node' 
      ? USER_CLASS_COLORS.default.node 
      : USER_CLASS_COLORS.default.link;
  }
  
  const normalized = userClass.toLowerCase().trim();
  const colorScheme = USER_CLASS_COLORS[normalized] || USER_CLASS_COLORS.default;
  return type === 'node' ? colorScheme.node : colorScheme.link;
};

export const getHeatmapColor = (value, maxValue, isLink = false) => {
  if (!value || !maxValue || maxValue === 0) {
    return isLink ? 'rgba(107, 114, 128, 0.2)' : '#6b7280';
  }
  
  const normalized = Math.min(value / maxValue, 1);
  const { gradient, thresholds } = HEATMAP_COLORS;
  
  for (let i = 0; i < thresholds.length; i++) {
    if (normalized <= thresholds[i]) {
      const color = gradient[i];
      if (isLink) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, 0.4)`;
      }
      return color;
    }
  }
  
  const color = gradient[gradient.length - 1];
  if (isLink) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.4)`;
  }
  return color;
};

export const getXAxisLabel = (dataFormat) => {
  switch (dataFormat) {
    case 'velocity-pivot':
      return 'Speed (mph)';
    case 'time-series':
      return 'Time';
    case 'velocity-bucket':
      return 'Speed (mph)';
    case 'kpi-velocity':
      return 'Speed Bucket';
    case 'velocity-percentile':
      return 'Time';
    case 'sankey':
      return '';
    default:
      return '';
  }
};

export const getXAxisKey = (dataFormat, data) => {
  switch (dataFormat) {
    case 'velocity-pivot':
      return 'VelBucket';
    case 'time-series':
      return 'event_time';
    case 'velocity-bucket':
      return 'VelBucket';
    case 'kpi-velocity':
      return 'speed';
    case 'velocity-percentile':
      return 'event_time';
    case 'sankey':
      return null;
    default: {
      const firstItem = data[0];
      return Object.keys(firstItem).find(key => 
        typeof firstItem[key] !== 'number'
      ) || 'name';
    }
  }
};

export const transformSankeyData = (data, sortBy = 'name') => {
  if (data.nodes && data.links) {
    return data;
  }
  
  const sourceNodes = new Set();
  const targetNodes = new Set();
  const nodeUserClasses = {};
  
  data.forEach(item => {
    sourceNodes.add(item.source);
    targetNodes.add(item.target);
    if (item.userClass) {
      nodeUserClasses[item.source] = item.userClass;
      nodeUserClasses[item.target] = item.userClass;
    }
  });
  
  const nodeValues = {};
  data.forEach(item => {
    nodeValues[item.source] = (nodeValues[item.source] || 0) + item.value;
    nodeValues[item.target] = (nodeValues[item.target] || 0) + item.value;
  });
  
  const sortFn = (a, b) => {
    if (sortBy === 'value') {
      return (nodeValues[b] || 0) - (nodeValues[a] || 0);
    }
    return a.localeCompare(b);
  };
  
  const sortedSources = Array.from(sourceNodes).sort(sortFn);
  const sortedTargets = Array.from(targetNodes).sort(sortFn);
  
  const allNodes = [...sortedSources];
  sortedTargets.forEach(target => {
    if (!sourceNodes.has(target)) {
      allNodes.push(target);
    }
  });
  
  const nodes = allNodes.map(name => ({ 
    name,
    userClass: nodeUserClasses[name] || null
  }));
  
  const nodeIndexMap = {};
  nodes.forEach((node, index) => {
    nodeIndexMap[node.name] = index;
  });
  
  const links = data.map(item => ({
    source: nodeIndexMap[item.source],
    target: nodeIndexMap[item.target],
    value: item.value,
    userClass: item.userClass || null
  }));

  return { nodes, links };
};

export const transformKPIData = (data) => {
  const speedBuckets = [...new Set(data.map(item => item.speedMPHBucket))].sort((a, b) => a - b);
  const vehTypes = [...new Set(data.map(item => item.veh_type))];

  return speedBuckets.map(speed => {
    const bucketData = { speed: `${speed} mph` };
    vehTypes.forEach(type => {
      const entry = data.find(d => d.speedMPHBucket === speed && d.veh_type === type);
      // FIXED: No optional chaining
      bucketData[type] = entry ? entry.veh_count : 0; 
    });
    return bucketData;
  });
};

export const transformSankeyDataMultiColumn = (data, sortBy = 'name') => {
  if (data.nodes && data.links) {
    return data;
  }
  
  // Source (entry) and target (exit) nodes are kept in separate namespaces.
  // The same label (e.g. a street/side/lane) is naturally an entry for some
  // movements and an exit for others; keying nodes by label alone would
  // collapse those into one node, giving it both incoming and outgoing links
  // and producing backward/overlapping flows. Namespacing forces a clean
  // bipartite left(entry)->right(exit) layout. `name` stays the clean label
  // for display.
  const SRC = 'src ';
  const TGT = 'tgt ';

  const sourceKeys = new Set();
  const targetKeys = new Set();
  const nodeUserClasses = {};
  const nodeValues = {};

  data.forEach(item => {
    const sKey = SRC + item.source;
    const tKey = TGT + item.target;
    sourceKeys.add(sKey);
    targetKeys.add(tKey);
    nodeValues[sKey] = (nodeValues[sKey] || 0) + item.value;
    nodeValues[tKey] = (nodeValues[tKey] || 0) + item.value;
    if (item.userClass) {
      nodeUserClasses[sKey] = item.userClass;
      nodeUserClasses[tKey] = item.userClass;
    }
  });

  const sortFn = (a, b) => {
    if (sortBy === 'value') {
      return (nodeValues[b] || 0) - (nodeValues[a] || 0);
    }
    return a.localeCompare(b);
  };

  // Entry nodes on the left, exit nodes on the right.
  const allKeys = [
    ...Array.from(sourceKeys).sort(sortFn),
    ...Array.from(targetKeys).sort(sortFn),
  ];

  const nodeIndexMap = {};
  const nodes = allKeys.map((key, index) => {
    nodeIndexMap[key] = index;
    return {
      name: key.slice(key.indexOf(' ') + 1),
      userClass: nodeUserClasses[key] || null,
    };
  });

  const links = data.map(item => ({
    source: nodeIndexMap[SRC + item.source],
    target: nodeIndexMap[TGT + item.target],
    value: item.value,
    userClass: item.userClass || null
  }));

  return { nodes, links };
};